import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@components/Header';
import Footer from '@components/Footer';
import ProfileCard from '@components/Profile/ProfileCard';
import { useAuth } from '@hooks/useAuth';
import { useVIPStatus } from '@hooks/useVIPStatus';
import { useWatchHistory } from '@hooks/useWatchHistory';
import { getCoverImageUrl, isMobile } from '@utils/helpers';

function Profile() {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { isVIP, vipInfo, isLoading: vipLoading } = useVIPStatus();
    const { history, isLoading: historyLoading } = useWatchHistory(20);
    const [activeTab, setActiveTab] = useState('info');

    if (!user) {
        navigate('/login');
        return null;
    }

    // Show loading state briefly if VIP status is loading AND we don't have cached data
    const showVIPLoading = vipLoading && !vipInfo;

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const handleHistoryClick = (item) => {
        const mobile = isMobile() ? '&mobile=1' : '';
        const title = item.book_name || `Drama ${item.book_id}`;
        const titleParam = `&title=${encodeURIComponent(title)}`;
        navigate(`/player?bookId=${item.book_id}&startChapter=${item.chapter_id}${titleParam}${mobile}`);
    };

    // Format time helper
    const formatTime = (seconds) => {
        if (!seconds || seconds === 0) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="profile-page">
            <Header />

            <main className="main-content" style={{ paddingTop: '120px', minHeight: '70vh', maxWidth: '1000px', margin: '0 auto', padding: '120px 1rem 2rem' }}>
                <div style={{ background: 'rgba(26, 26, 26, 0.9)', borderRadius: '12px', padding: '2rem', marginBottom: '2rem' }}>
                    {/* Profile Card (Gold/Silver) */}
                    {showVIPLoading ? (
                        <div style={{ padding: '3rem 0', textAlign: 'center', color: '#999' }}>
                            <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
                            <p>Memuat data profil...</p>
                        </div>
                    ) : (
                        <ProfileCard 
                            isVIP={isVIP} 
                            username={user.username || user.email?.split('@')[0] || 'User'}
                            vipInfo={vipInfo}
                        />
                    )}

                    {/* Tabs */}
                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        <button
                            onClick={() => setActiveTab('info')}
                            style={{
                                padding: '0.75rem 1.5rem',
                                background: 'none',
                                border: 'none',
                                borderBottom: activeTab === 'info' ? '2px solid #d4af37' : '2px solid transparent',
                                color: activeTab === 'info' ? '#d4af37' : '#999',
                                cursor: 'pointer',
                                transition: 'all 0.3s',
                                fontSize: '1rem',
                                fontWeight: activeTab === 'info' ? '600' : '400'
                            }}
                        >
                            <i className="fas fa-user"></i> Informasi
                        </button>
                        <button
                            onClick={() => setActiveTab('history')}
                            style={{
                                padding: '0.75rem 1.5rem',
                                background: 'none',
                                border: 'none',
                                borderBottom: activeTab === 'history' ? '2px solid #d4af37' : '2px solid transparent',
                                color: activeTab === 'history' ? '#d4af37' : '#999',
                                cursor: 'pointer',
                                transition: 'all 0.3s',
                                fontSize: '1rem',
                                fontWeight: activeTab === 'history' ? '600' : '400'
                            }}
                        >
                            <i className="fas fa-history"></i> Riwayat Tontonan
                        </button>
                    </div>

                    {/* Tab Content */}
                    {activeTab === 'info' && (
                        <div>
                            <h3 style={{ marginBottom: '1.5rem', fontSize: '1.5rem' }}>Informasi Akun</h3>
                            <div style={{ display: 'grid', gap: '1rem' }}>
                                <div style={{ padding: '1rem', background: 'rgba(0,0,0,0.3)', borderRadius: '8px' }}>
                                    <label style={{ color: '#999', display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Username</label>
                                    <p style={{ margin: 0, fontSize: '1.1rem' }}>{user.username || '-'}</p>
                                </div>
                                <div style={{ padding: '1rem', background: 'rgba(0,0,0,0.3)', borderRadius: '8px' }}>
                                    <label style={{ color: '#999', display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Email</label>
                                    <p style={{ margin: 0, fontSize: '1.1rem' }}>{user.email}</p>
                                </div>
                                <div style={{ padding: '1rem', background: 'rgba(0,0,0,0.3)', borderRadius: '8px' }}>
                                    <label style={{ color: '#999', display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Status Keanggotaan</label>
                                    <p style={{ margin: 0, color: isVIP ? '#d4af37' : '#fff', fontSize: '1.1rem', fontWeight: '600' }}>
                                        {isVIP ? '👑 VIP Member' : '👤 Regular Member'}
                                    </p>
                                </div>
                            </div>

                            <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                                <button
                                    onClick={handleLogout}
                                    className="auth-btn logout-btn"
                                    style={{ width: '100%', padding: '0.75rem', fontSize: '1rem' }}
                                >
                                    <i className="fas fa-sign-out-alt"></i> Logout
                                </button>
                            </div>
                        </div>
                    )}

                    {activeTab === 'history' && (
                        <div>
                            <h3 style={{ marginBottom: '1.5rem', fontSize: '1.5rem' }}>
                                <i className="fas fa-history"></i> Riwayat Tontonan
                            </h3>
                            
                            {historyLoading ? (
                                <div style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>
                                    <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', marginBottom: '1rem', display: 'block' }}></i>
                                    <p>Memuat riwayat...</p>
                                </div>
                            ) : history && history.length > 0 ? (
                                <div style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', 
                                    gap: '1rem' 
                                }}>
                                    {history.map((item, index) => {
                                        const cover = item.cover_url || getCoverImageUrl({ id: item.book_id });
                                        const title = item.book_name || `Drama ${item.book_id}`;
                                        
                                        // Calculate progress
                                        let progressPercent = 0;
                                        if (item.duration_seconds && item.duration_seconds > 0) {
                                            progressPercent = Math.min(100, Math.round((item.position_seconds / item.duration_seconds) * 100));
                                        }

                                        return (
                                            <div
                                                key={`${item.book_id}-${item.chapter_id}-${index}`}
                                                onClick={() => handleHistoryClick(item)}
                                                style={{
                                                    background: 'rgba(0,0,0,0.3)',
                                                    borderRadius: '12px',
                                                    overflow: 'hidden',
                                                    cursor: 'pointer',
                                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                                    transform: 'translateZ(0)'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-5px)';
                                                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(255, 215, 0, 0.3)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = 'none';
                                                }}
                                            >
                                                {/* Poster */}
                                                <div style={{ position: 'relative', paddingTop: '140%', overflow: 'hidden', background: '#1a1a1a' }}>
                                                    <img
                                                        src={cover}
                                                        alt={title}
                                                        loading="lazy"
                                                        onError={(e) => {
                                                            e.target.src = 'https://via.placeholder.com/300x400/1a1a1a/666?text=No+Image';
                                                        }}
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                    
                                                    {/* Progress bar */}
                                                    {progressPercent > 0 && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            bottom: 0,
                                                            left: 0,
                                                            right: 0,
                                                            height: '4px',
                                                            background: 'rgba(255, 255, 255, 0.2)',
                                                            zIndex: 2
                                                        }}>
                                                            <div style={{
                                                                height: '100%',
                                                                width: `${progressPercent}%`,
                                                                background: 'linear-gradient(90deg, #FFD700 0%, #FFA500 100%)',
                                                                transition: 'width 0.3s ease'
                                                            }}></div>
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                {/* Info */}
                                                <div style={{ padding: '0.75rem' }}>
                                                    <h4 style={{ 
                                                        margin: '0 0 0.25rem 0', 
                                                        fontSize: '0.9rem',
                                                        fontWeight: '600',
                                                        color: '#fff',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        lineHeight: '1.3'
                                                    }}>
                                                        {title}
                                                    </h4>
                                                    <p style={{ 
                                                        margin: '0 0 0.25rem 0', 
                                                        fontSize: '0.8rem',
                                                        color: '#FFD700',
                                                        fontWeight: '500'
                                                    }}>
                                                        Episode {item.chapter_id || '?'}
                                                    </p>
                                                    {item.duration_seconds > 0 && (
                                                        <p style={{ 
                                                            margin: 0, 
                                                            fontSize: '0.75rem',
                                                            color: 'rgba(255, 255, 255, 0.5)'
                                                        }}>
                                                            {formatTime(item.position_seconds)} / {formatTime(item.duration_seconds)}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div style={{ 
                                    textAlign: 'center', 
                                    padding: '4rem 1rem',
                                    color: '#999'
                                }}>
                                    <i className="fas fa-film" style={{ 
                                        fontSize: '4rem', 
                                        marginBottom: '1rem',
                                        display: 'block',
                                        opacity: 0.3
                                    }}></i>
                                    <h4 style={{ fontSize: '1.3rem', margin: '0 0 0.5rem 0' }}>
                                        Belum ada riwayat tontonan
                                    </h4>
                                    <p style={{ margin: '0 0 1.5rem 0' }}>
                                        Mulai menonton drama untuk melihat riwayat di sini
                                    </p>
                                    <button
                                        onClick={() => navigate('/')}
                                        className="auth-btn primary"
                                        style={{ padding: '0.75rem 1.5rem' }}
                                    >
                                        <i className="fas fa-home"></i> Kembali ke Beranda
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
}

export default Profile;
